import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI } from '../../services/api';

// Matches app/services/rancher_client.py:auth_failure_detail() — the one message that
// means "the stored access credential is dead", as opposed to any other resource-fetch
// error. Narrow on purpose: an ordinary RBAC denial should not offer a fix-access
// button that cannot help it. Wording is deliberately platform-agnostic — the backend
// talks to Rancher, but nothing in the UI should say so.
const isAccessCredentialFailure = (msg) =>
  typeof msg === 'string' && msg.includes('is no longer valid');

function StatusBadge({ status, style = {} }) {
  const colors = {
    ready: 'var(--color-success-500)', Ready: 'var(--color-success-500)',
    running: 'var(--color-success-500)', Running: 'var(--color-success-500)',
    active: 'var(--color-success-500)',
    pending: 'var(--color-warning-500)', Pending: 'var(--color-warning-500)',
    notready: 'var(--color-danger-500)', NotReady: 'var(--color-danger-500)',
    failed: 'var(--color-danger-500)', Failed: 'var(--color-danger-500)',
    provisioning: 'var(--brand)', connecting: 'var(--accent)',
    installing: 'var(--accent)', succeeded: 'var(--color-success-500)', Succeeded: 'var(--color-success-500)',
  };
  const bg = colors[status] || 'var(--muted)';
  return (
    <span className="status-badge" style={{ background: bg, color: 'white', ...style }}>
      {status}
    </span>
  );
}

// ── Imported cluster live resources view ──────────────────────────────────────

export function ImportedClusterView({ clusterId, hasVpn }) {
  const [activeTab, setActiveTab] = useState('nodes');
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nsFilter, setNsFilter] = useState('');
  const [podFilter, setPodFilter] = useState('');
  const [podNsFilter, setPodNsFilter] = useState('all');

  const [retryCount, setRetryCount] = useState(0);
  const MAX_AUTO_RETRIES = 3;

  const [repairing, setRepairing] = useState(false);
  const [repairMsg, setRepairMsg] = useState('');

  const loadResources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await clustersAPI.getResources(clusterId);
      setResources(res.data);
      setError(null);
      setRetryCount(0);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // detail may be an object {message, action_required} for agent-not-connected case
      const msg = typeof detail === 'object'
        ? (detail.message || 'Agent not connected')
        : (detail || 'Failed to fetch cluster resources');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { loadResources(); }, [loadResources]);

  // Auto-retry with backoff — max 3 times, then stop to avoid hammering the API.
  // User can manually retry via the Refresh button. Skipped for a dead access
  // credential: that error will not resolve itself between retries, only via
  // repair/bootstrap below.
  useEffect(() => {
    if (!error || resources || retryCount >= MAX_AUTO_RETRIES || isAccessCredentialFailure(error)) return;
    const delay = 3000 * Math.pow(2, retryCount); // 3s, 6s, 12s
    const t = setTimeout(() => {
      setRetryCount(c => c + 1);
      loadResources();
    }, delay);
    return () => clearTimeout(t);
  }, [error, resources, retryCount, loadResources]);

  const handleRepairToken = async () => {
    setRepairing(true);
    setRepairMsg('');
    try {
      await clustersAPI.repairRancherToken(clusterId);
      setRepairMsg('Repaired — reloading...');
      await loadResources();
    } catch (e) {
      if (e.response?.status === 409) {
        // No still-working credential exists anywhere for this cluster to self-heal
        // from. Deliberately NOT prompting for one here: that would mean asking
        // someone to type a management-platform credential into SarvKube, which is
        // exactly what this flow must never do. This is an infra-level fix — direct
        // access to the cluster's management server, outside of any SarvKube UI.
        setRepairMsg(
          'This needs an infra-level fix — no credential this app already trusts can ' +
          'repair it automatically. Contact whoever administers this cluster directly.'
        );
      } else {
        setRepairMsg(e.response?.data?.detail || e.message || 'Repair failed.');
      }
    } finally {
      setRepairing(false);
    }
  };

  if (loading && !resources) return (
    <div className="resources-loading">
      {hasVpn ? 'Bringing up VPN tunnel and connecting to cluster...' : 'Connecting to cluster...'}
    </div>
  );
  if (error && !resources) {
    const authFailure = isAccessCredentialFailure(error);
    return (
      <div className="banner danger" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14 }}>
            {error.includes('not connected') || error.includes('Agent')
              ? 'Agent not connected — install sarvkube-agent on the cluster master node'
              : error}
            {!authFailure && retryCount < MAX_AUTO_RETRIES && (
              <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: 12 }}>
                (retrying...)
              </span>
            )}
          </span>
          {authFailure && (
            <button className="btn btn-secondary btn-sm" disabled={repairing} onClick={handleRepairToken}>
              {repairing ? 'Checking...' : 'Try to Fix Automatically'}
            </button>
          )}
          {!authFailure && (
            <button className="btn btn-secondary btn-sm"
              onClick={() => { setRetryCount(0); loadResources(); }}>
              Retry
            </button>
          )}
        </div>
        {repairMsg && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{repairMsg}</div>
        )}
      </div>
    );
  }
  if (!resources) return null;

  const { nodes, namespaces, pods, summary } = resources;

  const podNamespaces = ['all', ...new Set(pods.map(p => p.namespace))].sort();

  const filteredPods = pods.filter(p => {
    const nsMatch = podNsFilter === 'all' || p.namespace === podNsFilter;
    const nameMatch = p.name.toLowerCase().includes(podFilter.toLowerCase());
    return nsMatch && nameMatch;
  });

  const filteredNs = namespaces.filter(n =>
    n.name.toLowerCase().includes(nsFilter.toLowerCase())
  );

  return (
    <div>
      {/* Summary cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-number">{summary.node_count}</div>
          <div className="summary-label">Nodes</div>
        </div>
        <div className="summary-card">
          <div className="summary-number">{summary.namespace_count}</div>
          <div className="summary-label">Namespaces</div>
        </div>
        <div className="summary-card">
          <div className="summary-number">{summary.pod_count}</div>
          <div className="summary-label">Pods</div>
        </div>
        <div className="summary-card summary-card-green">
          <div className="summary-number">{summary.pods_running}</div>
          <div className="summary-label">Running</div>
        </div>
        <div className="summary-card summary-card-red">
          <div className="summary-number">{summary.pods_failed}</div>
          <div className="summary-label">Failed</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {[
          { key: 'nodes',      label: `Nodes (${nodes.length})` },
          { key: 'namespaces', label: `Namespaces (${namespaces.length})` },
          { key: 'pods',       label: `Pods (${pods.length})` },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-btn ${activeTab === t.key ? 'tab-btn-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <button className="btn btn-sm btn-secondary tab-refresh" onClick={loadResources}>
          Refresh
        </button>
      </div>

      {/* Nodes tab */}
      {activeTab === 'nodes' && (
        <div className="table-card">
        <table className="ds">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Roles</th>
              <th>Version</th>
              <th>OS</th>
              <th>CPU</th>
              <th>Memory</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map(n => (
              <tr key={n.name}>
                <td><code style={{ fontSize: 13 }}>{n.name}</code></td>
                <td><StatusBadge status={n.status} /></td>
                <td>{n.roles.map(r => (
                  <span key={r} className="role-chip">{r}</span>
                ))}</td>
                <td>{n.version}</td>
                <td style={{ fontSize: 12, color: 'var(--muted)' }}>{n.os}</td>
                <td>{n.cpu}</td>
                <td style={{ fontSize: 12 }}>{n.memory}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Namespaces tab */}
      {activeTab === 'namespaces' && (
        <>
          <div className="tab-filter">
            <input
              type="text"
              placeholder="Filter namespaces..."
              value={nsFilter}
              onChange={e => setNsFilter(e.target.value)}
            />
          </div>
          <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredNs.map(n => (
                <tr key={n.name}>
                  <td><code style={{ fontSize: 13 }}>{n.name}</code></td>
                  <td><StatusBadge status={n.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {n.created_at ? new Date(n.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {/* Pods tab */}
      {activeTab === 'pods' && (
        <>
          <div className="tab-filter">
            <select value={podNsFilter} onChange={e => setPodNsFilter(e.target.value)}>
              {podNamespaces.map(ns => (
                <option key={ns} value={ns}>{ns === 'all' ? 'All namespaces' : ns}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Filter pods..."
              value={podFilter}
              onChange={e => setPodFilter(e.target.value)}
            />
          </div>
          <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                <th>Name</th>
                <th>Namespace</th>
                <th>Status</th>
                <th>Ready</th>
                <th>Restarts</th>
                <th>Node</th>
              </tr>
            </thead>
            <tbody>
              {filteredPods.map(p => (
                <tr key={`${p.namespace}/${p.name}`}>
                  <td><code style={{ fontSize: 12 }}>{p.name}</code></td>
                  <td style={{ fontSize: 13, color: 'var(--ink-2)' }}>{p.namespace}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td>{p.ready}</td>
                  <td style={{ color: p.restarts > 0 ? 'var(--color-danger-500)' : 'var(--color-success-500)' }}>{p.restarts}</td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{p.node}</td>
                </tr>
              ))}
              {filteredPods.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 24 }}>No pods match filter</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
